import {env} from "next-runtime-env";
import {isListingKey} from "@/lib/listing-filters";
import {BROKERAGE_LOGO_READY} from "@/lib/brokerage";
import type {PublicSettings} from "@/lib/public-data";

/**
 * Server-side client of the REALTOR.ca listing search API (`/api/v1/listings/*`,
 * issue #139, Contract 4). Calls are never cached (see `getJson`). Import this only from server components.
 */

/** One listing in a search result; also the common part of a listing's detail. */
export type ListingSummary = {
    listing_key: string;
    mls_number: string | null;
    slug: string;
    address: string | null;
    city: string | null;
    city_region: string | null;
    subdivision: string | null;
    /** Money as a string with two decimals. */
    price: string | null;
    bedrooms: number | null;
    bathrooms: number | null;
    /** Not sent by older API versions. */
    year_built?: number | null;
    property_type: string | null;
    living_area: string | null;
    living_area_units: string | null;
    photo: { url: string; description: string | null } | null;
    /** The listing brokerage; never empty. */
    office_name: string;
    /** The listing on REALTOR.ca; the target of the logo link. */
    listing_url: string | null;
};

export type ListingMedia = {
    url: string;
    order: number;
    preferred: boolean;
    category: string | null;
    description: string | null;
};

export type ListingRoom = { type: string | null; level: string | null; dimensions: string | null };

export type ListingOpenHouse = {
    date: string;
    start_time: string | null;
    end_time: string | null;
    type: string | null;
    remarks: string | null;
};

export type ListingDetail = ListingSummary & {
    media: ListingMedia[];
    /** Shown exactly as received. */
    public_remarks: string | null;
    structure_type: string[] | null;
    common_interest: string | null;
    lot_size_area: string | null;
    lot_size_units: string | null;
    postal_code: string | null;
    province: string | null;
    rooms: ListingRoom[];
    open_houses: ListingOpenHouse[];
    original_entry_ts: string | null;
    modification_ts: string | null;
    /** The fields below arrive with the #155 API; until then they are missing and shown as N/A or left out. */
    tax_annual_amount?: string | null;
    tax_year?: number | null;
    status?: string | null;
    features?: { label: string; values: string[] }[] | null;
    /** False when the seller asked to hide the address: no map is shown then. Missing means true. */
    address_display?: boolean | null;
};

export type ListingSearch = {
    items: ListingSummary[];
    total: number;
    page: number;
    page_size: number;
    data_updated_at: string;
};

export type ListingArea = {
    slug: string;
    name: string;
    kind: "city" | "city_region";
    count: number;
    subdivisions: { slug: string; name: string; count: number }[];
};

export type ListingAreas = {
    areas: ListingArea[];
    types: { slug: string; name: string; count: number }[];
};

export type ListingKey = { listing_key: string; slug: string; modification_ts: string | null };

/**
 * Outcome of an API call. `off` means the listing search is switched off (the page
 * answers 404); `not-found` is an unknown listing; `unavailable` means the data is
 * stale (503) or the API could not be reached.
 */
export type ListingsResult<T> =
    | { status: "ok"; data: T }
    | { status: "off" }
    | { status: "not-found" }
    | { status: "unavailable" };

/** The site's path of a listing: `/listings/<key>/<slug>`. */
export function listingPath(item: { listing_key: string; slug: string }): string {
    return `/listings/${encodeURIComponent(item.listing_key)}/${encodeURIComponent(item.slug)}`;
}

async function getJson<T>(path: string, query?: URLSearchParams): Promise<ListingsResult<T>> {
    // Read outside the try: at build time env() opts the route out of prerendering by throwing.
    const apiUri = env("NEXT_PUBLIC_API_URI");
    const url = `${apiUri}/api/v1/listings/${path}${query && query.size > 0 ? `?${query}` : ""}`;
    try {
        // Never cached: a cached 200 would outlive a 404 (listing removed, search switched off) or a 503
        // (stale data), which Next does not replace in its data cache. The pages are dynamic and
        // nginx and the API limits bound the load.
        const res = await fetch(url, {cache: "no-store"});
        if (res.status === 404) return {status: path === "search" || path === "areas" || path === "keys" ? "off" : "not-found"};
        if (res.status === 503) return {status: "unavailable"};
        if (!res.ok) throw new Error(`listings/${path.split("/")[0]} request failed: ${res.status}`);
        return {status: "ok", data: (await res.json()) as T};
    } catch (err) {
        console.error(err);
        return {status: "unavailable"};
    }
}

/** A page of search results; `params` carries the already validated filters. */
export function searchListings(params: URLSearchParams): Promise<ListingsResult<ListingSearch>> {
    return getJson<ListingSearch>("search", params);
}

/** One listing by its key; the caller checks the slug. */
export async function getListingDetail(key: string): Promise<ListingsResult<ListingDetail>> {
    if (!isListingKey(key)) return {status: "not-found"};
    const result = await getJson<ListingDetail>(encodeURIComponent(key));
    // Anything that is not this listing's detail (a different shape) counts as not found.
    if (result.status === "ok" && (result.data?.listing_key !== key || !Array.isArray(result.data.media))) {
        return {status: "not-found"};
    }
    return result;
}

/** What an MLS® number looks like: letters and digits, at most 32 (the API's limit). */
const MLS_SHAPE = /^[A-Za-z0-9]{1,32}$/;

/**
 * Where an old `/listingdetail/<MLS® number>` link goes now: the listing's key and slug, from
 * either REALTOR.ca feed (`GET /listings/mls/{number}`, case-insensitive). `not-found` is an
 * unknown listing or the search being off (the API answers the same 404).
 */
export async function getListingByMls(mls: string): Promise<ListingsResult<{ listing_key: string; slug: string }>> {
    if (!MLS_SHAPE.test(mls)) return {status: "not-found"};
    const result = await getJson<{ listing_key?: unknown; slug?: unknown }>(`mls/${encodeURIComponent(mls)}`);
    if (result.status !== "ok") return result;
    const {listing_key: key, slug} = result.data;
    if (typeof key !== "string" || typeof slug !== "string" || !key || !slug) return {status: "not-found"};
    return {status: "ok", data: {listing_key: key, slug}};
}

/** The areas and property types for the filters, with counts. */
export function getListingAreas(): Promise<ListingsResult<ListingAreas>> {
    return getJson<ListingAreas>("areas");
}

/** `GET /api/v1/listings/keys/summary`: how the sitemap's listing keys are paged. */
export type ListingKeysSummary = { total: number; page_size: number };

/** How long the sitemap reuses the listing keys (seconds). */
export const SITEMAP_REVALIDATE_SECONDS = 900;

/**
 * A sitemap read of the listing API, kept in Next's data cache for
 * {@link SITEMAP_REVALIDATE_SECONDS} (tag `listings`), so crawlers re-reading
 * the sitemap don't reach the API each time. Each keys page is about 1 MB,
 * under the cache's 2 MB item limit. Only 200 answers are kept.
 */
async function getSitemapJson<T>(path: string): Promise<ListingsResult<T>> {
    const apiUri = env("NEXT_PUBLIC_API_URI");
    try {
        const res = await fetch(`${apiUri}/api/v1/listings/${path}`, {
            next: {revalidate: SITEMAP_REVALIDATE_SECONDS, tags: ["listings"]},
        });
        if (res.status === 404) return {status: "off"};
        if (res.status === 503) return {status: "unavailable"};
        if (!res.ok) throw new Error(`listings/${path.split("?")[0]} request failed: ${res.status}`);
        return {status: "ok", data: (await res.json()) as T};
    } catch (err) {
        console.error(err);
        return {status: "unavailable"};
    }
}

/** The number of listing keys and the keys per sitemap page. */
export function getListingKeysSummary(): Promise<ListingsResult<ListingKeysSummary>> {
    return getSitemapJson<ListingKeysSummary>("keys/summary");
}

/** One page (from 1) of listing keys for one sitemap file. */
export function getListingKeysPage(page: number): Promise<ListingsResult<ListingKey[]>> {
    return getSitemapJson<ListingKey[]>(`keys?page=${page}`);
}

/** One home page carousel card: a search item plus whether the owner pinned it. */
export type CarouselItem = ListingSummary & { pinned: boolean };

/** `GET /api/v1/carousel`: the office feed's listings chosen for the home page. */
export type Carousel = {
    items: CarouselItem[];
    /** The number of listings the carousel is set to show. */
    count: number;
    data_updated_at: string;
};

/**
 * True when the home page may show the office-feed carousel: both the carousel
 * switch and the listing search are on, and the brokerage logo is in place (the
 * cards link to the listing pages, which need all three). Otherwise the home page shows
 * only the Listings heading and the "Search all listings" button.
 */
export function memberCarouselVisible(
    settings: Pick<PublicSettings, "ddf_public_enabled" | "carousel_from_member_feed">,
): boolean {
    return (
        settings.carousel_from_member_feed === "true" &&
        settings.ddf_public_enabled === "true" &&
        BROKERAGE_LOGO_READY
    );
}

/**
 * The office-feed carousel, fetched on every request. `off` is a 404 (switched off), `unavailable` a 503 or an
 * unreachable API; the home page then shows only the heading and the button.
 */
export async function getCarousel(): Promise<ListingsResult<Carousel>> {
    // Read outside the try, see getJson.
    const apiUri = env("NEXT_PUBLIC_API_URI");
    try {
        // Not cached, for the same reason as `getJson`: the 404 after a switch-off must show at once.
        const res = await fetch(`${apiUri}/api/v1/carousel`, {cache: "no-store"});
        if (res.status === 404) return {status: "off"};
        if (res.status === 503) return {status: "unavailable"};
        if (!res.ok) throw new Error(`carousel request failed: ${res.status}`);
        const data = (await res.json()) as Carousel;
        if (!Array.isArray(data?.items)) throw new Error("carousel answer has no items");
        return {status: "ok", data};
    } catch (err) {
        console.error(err);
        return {status: "unavailable"};
    }
}
